/**
 * Facturas.
 *
 * «Vencida» es una factura **emitida** cuyo vencimiento ha pasado. Un
 * borrador vencido no es deuda: es un descuido de facturación, y mezclar
 * los dos infla la cifra de morosidad que después se reclama.
 */

import { InvoiceSeries } from './invoice-series.js';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../table/data-table.js';
import { useListing } from '../table/use-listing.js';
import { money, day } from '../table/format.js';

interface Row {
  id: string;
  number: string;
  invoice_type: string;
  status: string;
  issue_date: string;
  due_date: string | null;
  customer_name: string;
  series_code: string;
  taxable_base: string;
  tax_total: string;
  total: string;
  currency: string;
  days_overdue: number | null;
}

const STATUS: Record<string, { label: string; tone: string; icon: string }> = {
  BORRADOR: { label: 'Borrador', tone: 'off', icon: '•' },
  EMITIDA: { label: 'Emitida', tone: 'ok', icon: '✓' },
  ANULADA: { label: 'Anulada', tone: 'bad', icon: '✕' },
  UNKNOWN: { label: 'Sin clasificar', tone: 'off', icon: '?' },
};

const COLUMNS: Column<Row>[] = [
  { id: 'number', label: 'Nº', render: (r) => r.number, sortable: true, width: '11rem' },
  { id: 'seriesCode', label: 'Serie', render: (r) => r.series_code, width: '6rem' },
  { id: 'invoiceType', label: 'Tipo', render: (r) => r.invoice_type, width: '5rem' },
  { id: 'customerName', label: 'Cliente', render: (r) => r.customer_name },
  {
    id: 'status',
    label: 'Estado',
    width: '8rem',
    render: (r) => {
      const s = STATUS[r.status] ?? STATUS['UNKNOWN']!;
      return <span className={`pill pill--${s.tone}`}>{`${s.icon} ${s.label}`}</span>;
    },
    exportValue: (r) => (STATUS[r.status] ?? STATUS['UNKNOWN']!).label,
  },
  { id: 'issueDate', label: 'Emitida', render: (r) => day(r.issue_date), sortable: true, width: '8rem' },
  {
    id: 'dueDate',
    label: 'Vencimiento',
    width: '13rem',
    render: (r) => {
      if (r.due_date === null) return <span className="due--none">—</span>;
      // Sólo una factura EMITIDA puede estar vencida como deuda.
      if (r.status === 'EMITIDA' && (r.days_overdue ?? 0) > 0) {
        return (
          <span className="due due--late">
            ⚠ {r.days_overdue} d · {r.due_date}
          </span>
        );
      }
      return <span className="due">{r.due_date}</span>;
    },
    exportValue: (r) => r.due_date ?? '',
  },
  {
    id: 'taxableBase',
    label: 'Base',
    numeric: true,
    render: (r) => money(r.taxable_base, r.currency),
    exportValue: (r) => r.taxable_base,
  },
  {
    id: 'total',
    label: 'Total',
    numeric: true,
    sortable: true,
    render: (r) => money(r.total, r.currency),
    exportValue: (r) => r.total,
  },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

const QUICK = [
  { id: 'all', label: 'Todas', param: {} },
  { id: 'overdue', label: 'Vencidas', param: { overdue: 'true' } },
  { id: 'rect', label: 'Rectificativas', param: { rectifications: 'true' } },
] as const;

export function InvoicesScreen(): React.ReactNode {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const quick = QUICK.find((q) => q.id === params.get('v')) ?? QUICK[0];

  const listing = useListing<Row>({
    path: '/invoices',
    columnIds: COLUMN_IDS,
    extraParams: quick.param,
    preserve: ['v'],
  });

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Facturas</h1>
        <div className="chips" role="group" aria-label="Filtros rápidos">
          {QUICK.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chip${quick.id === option.id ? ' chip--on' : ''}`}
              aria-pressed={quick.id === option.id}
              onClick={() => listing.setParam('v', option.id === 'all' ? null : option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <Link className="button button--primary button--small" to="/facturas/nueva">
          Nueva factura
        </Link>
        {listing.total !== null && <span className="screen__count">{listing.total} facturas</span>}
      </header>

      {listing.error !== null && (
        <p className="screen__error" role="alert">
          {listing.error}
        </p>
      )}

      <DataTable
        columns={COLUMNS}
        rows={listing.rows}
        idOf={(r) => r.id}
        state={listing.state}
        onStateChange={listing.setState}
        exportName="facturas"
        loading={listing.loading}
        hasMore={listing.hasMore}
        onLoadMore={listing.loadMore}
        totalMatching={listing.total}
        onOpenRow={(row) => void navigate(`/facturas/${row.id}`)}
        emptyMessage="No hay facturas que coincidan."
      />

      <InvoiceSeries />
    </section>
  );
}
